import { useEffect } from "react";
import { getIn, useFormik } from "formik";
import * as Yup from "yup";
import CustomButton from "../../../../components/CustomButton";
import DatePicker from "../../../../components/misc/DatePicker";
import TextInput from "../../../../components/misc/TextInput";

export interface RecallProductFormValues {
  productCode: string;
  lot: string;
  bbd: Date | null;
}

const initialLine: RecallProductFormValues = {
  productCode: "",
  lot: "",
  bbd: null,
};

export const recallProductSchema = Yup.object({
  productCode: Yup.string()
    .trim()
    .max(15, "Codul produsului poate avea maximum 15 caractere")
    .required("Campul este obligatoriu"),
  lot: Yup.string()
    .trim()
    .max(20, "Lotul poate avea maximum 20 de caractere")
    .required("Campul este obligatoriu"),
  bbd: Yup.date()
    .nullable()
    .typeError("Introdu o data valida")
    .required("Campul este obligatoriu"),
});

function RecallProductForm({
  disabled,
  onAdd,
  onPendingChange,
}: {
  disabled: boolean;
  onAdd: (line: RecallProductFormValues) => void;
  onPendingChange: (hasPendingProduct: boolean) => void;
}) {
  const {
    values,
    errors,
    touched,
    isValid,
    isSubmitting,
    getFieldProps,
    handleSubmit,
    setFieldValue,
    setFieldTouched,
  } = useFormik<RecallProductFormValues>({
    initialValues: initialLine,
    validationSchema: recallProductSchema,
    validateOnMount: true,
    onSubmit: async (line, { resetForm }) => {
      onAdd({
        ...line,
        productCode: line.productCode.trim(),
        lot: line.lot.trim(),
      });
      resetForm();
    },
  });

  useEffect(() => {
    onPendingChange(
      Boolean(values.productCode.trim() || values.lot.trim() || values.bbd),
    );
  }, [onPendingChange, values.productCode, values.lot, values.bbd]);

  return (
    <div className="mb-6 overflow-x-auto rounded-control border border-slate-200 p-4">
      <form
        onSubmit={handleSubmit}
        noValidate
        className="grid min-w-[760px] grid-cols-[1fr_1fr_1fr_auto] items-start gap-4">
        <TextInput
          label="Cod produs"
          name="productCode"
          inputProps={getFieldProps("productCode")}
          error={errors.productCode}
          touched={touched.productCode}
          disabled={disabled || isSubmitting}
          className="!mb-0"
        />
        <TextInput
          label="Lot"
          name="lot"
          inputProps={getFieldProps("lot")}
          error={errors.lot}
          touched={touched.lot}
          disabled={disabled || isSubmitting}
          className="!mb-0"
        />
        <DatePicker
          label="Data expirare (BBD)"
          value={values.bbd}
          isRequired
          withClearData
          gutterBottom={false}
          disabled={disabled || isSubmitting}
          error={getIn(errors, "bbd")}
          touched={getIn(touched, "bbd")}
          handleChangeDate={(value) => {
            setFieldTouched("bbd", true, false);
            setFieldValue("bbd", value);
          }}
        />
        <div className="pt-8">
          <CustomButton
            icon="ADD"
            type="submit"
            variant="outlined"
            disabled={!isValid || disabled || isSubmitting}>
            Adauga produs
          </CustomButton>
        </div>
      </form>
    </div>
  );
}

export default RecallProductForm;
